/**
 * LINE Flex Message Builder
 * Single-file version (ready to copy & paste)
 */

import type { ScheduleEntry } from '../types/schedule'
import type { Driver } from '../types/driver'

export type NotificationType =
  | 'assignment'
  | 'update'
  | 'cancellation'
  | 'reminder'

interface DayInfo {
  name: string
  shortName: string
  color: string
}

export class LineFlexMessageBuilder {
  private baseUrl: string

  constructor() {
    this.baseUrl = window.location.origin
  }

  /* -------------------- Day Info -------------------- */
  private getDayInfo(date: Date): DayInfo {
    const days: DayInfo[] = [
      { name: 'วันอาทิตย์', shortName: 'อาทิตย์', color: '#FF0000' },
      { name: 'วันจันทร์', shortName: 'จันทร์', color: '#FFD700' },
      { name: 'วันอังคาร', shortName: 'อังคาร', color: '#FF69B4' },
      { name: 'วันพุธ', shortName: 'พุธ', color: '#008000' },
      { name: 'วันพฤหัสบดี', shortName: 'พฤหัสบดี', color: '#FFA500' },
      { name: 'วันศุกร์', shortName: 'ศุกร์', color: '#1E90FF' },
      { name: 'วันเสาร์', shortName: 'เสาร์', color: '#800080' }
    ]
    return days[date.getDay()]
  }

  private formatThaiDate(date: Date): string {
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear() + 543}`
  }

  private formatThaiDateWithDay(date: Date): string {
    const day = this.getDayInfo(date)
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${day.shortName} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear() + 543}`
  }

  private formatTime(time: string): string {
    // Convert "08:30:00" or "08:30" to "08.30"
    if (!time) return ''
    const parts = time.split(':')
    if (parts.length >= 2) {
      return `${parts[0]}.${parts[1]}`
    }
    return time
  }

  /* -------------------- Assignment -------------------- */

  buildAssignmentMessage(schedule: ScheduleEntry, driver: Driver): any {
    const date = new Date(schedule.startDate)
    const vehicleImageUrl = schedule.vehicle?.imageUrl || 'https://via.placeholder.com/800x400/10B981/FFFFFF?text=No+Image'
    const dateText = this.formatThaiDateWithDay(date)

    return {
      type: 'flex',
      altText: '✨ แจ้งใช้งานขับรถ',
      contents: {
        type: 'bubble',
        size: 'mega',
        header: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'box',
              layout: 'horizontal',
              contents: [
                {
                  type: 'text',
                  text: '📄',
                  size: 'xl',
                  flex: 0
                },
                {
                  type: 'text',
                  text: 'แจ้งใช้งานขับรถ',
                  weight: 'bold',
                  size: 'lg',
                  color: '#FFFFFF',
                  margin: 'sm'
                }
              ]
            }
          ],
          backgroundColor: '#10B981',
          paddingAll: 'lg'
        },
        hero: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'image',
              url: vehicleImageUrl,
              size: 'full',
              aspectMode: 'cover',
              aspectRatio: '20:13'
            },
            {
              type: 'box',
              layout: 'vertical',
              contents: [
                {
                  type: 'box',
                  layout: 'vertical',
                  contents: [
                    {
                      type: 'text',
                      text: schedule.vehicle?.registrationNumber || 'ไม่ระบุ',
                      size: 'xs',
                      color: '#ffffff',
                      weight: 'bold'
                    }
                  ],
                  backgroundColor: '#1F2937',
                  cornerRadius: 'md',
                  paddingAll: '8px',
                  paddingStart: '12px',
                  paddingEnd: '12px'
                }
              ],
              position: 'absolute',
              offsetEnd: '18px',
              offsetBottom: '18px'
            }
          ],
          paddingAll: '0px'
        },
        body: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'text',
              text: `${driver.fullName}`,
              weight: 'bold',
              size: 'xl',
              color: '#1F2937'
            },
            {
              type: 'text',
              text: 'งานขับรถของคุณมีการเปลี่ยนแปลง',
              size: 'sm',
              color: '#6B7280',
              margin: 'sm'
            },
            {
              type: 'box',
              layout: 'vertical',
              margin: 'lg',
              spacing: 'sm',
              contents: [
                {
                  type: 'box',
                  layout: 'horizontal',
                  contents: [
                    {
                      type: 'text',
                      text: '🔔',
                      size: 'sm',
                      color: '#3B82F6',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: 'การเปลี่ยนแปลง:',
                      weight: 'bold',
                      color: '#3B82F6',
                      size: 'sm',
                      margin: 'sm',
                      flex: 0
                    }
                  ]
                },
                {
                  type: 'text',
                  text: '• หมายเหตุการเปลี่ยนแปลง',
                  size: 'sm',
                  color: '#374151',
                  wrap: true,
                  margin: 'sm'
                }
              ],
              backgroundColor: '#F3F4F6',
              cornerRadius: 'md',
              paddingAll: 'md'
            },
            {
              type: 'box',
              layout: 'vertical',
              margin: 'lg',
              spacing: 'md',
              contents: [
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '📅',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: dateText,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm',
                      weight: 'bold'
                    }
                  ]
                },
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '⏰',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: `${this.formatTime(schedule.startTime)} - ${this.formatTime(schedule.endTime)} น.`,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                },
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '🚙',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: schedule.vehicle?.registrationNumber || 'ไม่ระบุ',
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                },
                schedule.notes ? {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '📝',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: schedule.notes,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                } : null
              ].filter(Boolean)
            }
          ],
          paddingAll: 'xl'
        },
        footer: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'button',
              action: {
                type: 'uri',
                label: 'ดูรายละเอียดเพิ่มเติม',
                uri: `${this.baseUrl}/schedule-detail?id=${schedule.id}`
              },
              style: 'primary',
              color: '#10B981',
              height: 'sm'
            }
          ],
          paddingAll: 'md'
        }
      }
    }
  }

  /* -------------------- Update -------------------- */

  buildUpdateMessage(schedule: ScheduleEntry, driver: Driver, changes: string[]): any {
    const date = new Date(schedule.startDate)
    const vehicleImageUrl = schedule.vehicle?.imageUrl || 'https://via.placeholder.com/800x400/F97316/FFFFFF?text=No+Image'
    const dateText = this.formatThaiDateWithDay(date)

    return {
      type: 'flex',
      altText: '📝 แก้ไขงานขับรถ',
      contents: {
        type: 'bubble',
        size: 'mega',
        header: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'box',
              layout: 'horizontal',
              contents: [
                {
                  type: 'text',
                  text: '📝',
                  size: 'xl',
                  flex: 0
                },
                {
                  type: 'text',
                  text: 'แก้ไขงานขับรถ',
                  weight: 'bold',
                  size: 'lg',
                  color: '#FFFFFF',
                  margin: 'sm'
                }
              ]
            }
          ],
          backgroundColor: '#F97316',
          paddingAll: 'lg'
        },
        hero: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'image',
              url: vehicleImageUrl,
              size: 'full',
              aspectMode: 'cover',
              aspectRatio: '20:13'
            },
            {
              type: 'box',
              layout: 'vertical',
              contents: [
                {
                  type: 'box',
                  layout: 'vertical',
                  contents: [
                    {
                      type: 'text',
                      text: schedule.vehicle?.registrationNumber || 'ไม่ระบุ',
                      size: 'xs',
                      color: '#ffffff',
                      weight: 'bold'
                    }
                  ],
                  backgroundColor: '#1F2937',
                  cornerRadius: 'md',
                  paddingAll: '8px',
                  paddingStart: '12px',
                  paddingEnd: '12px'
                }
              ],
              position: 'absolute',
              offsetEnd: '18px',
              offsetBottom: '18px'
            }
          ],
          paddingAll: '0px'
        },
        body: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'text',
              text: `${driver.fullName}`,
              weight: 'bold',
              size: 'xl',
              color: '#1F2937'
            },
            {
              type: 'text',
              text: 'งานขับรถของคุณมีการเปลี่ยนแปลง',
              size: 'sm',
              color: '#6B7280',
              margin: 'sm'
            },
            {
              type: 'box',
              layout: 'vertical',
              margin: 'lg',
              spacing: 'sm',
              contents: [
                {
                  type: 'box',
                  layout: 'horizontal',
                  contents: [
                    {
                      type: 'text',
                      text: '🔔',
                      size: 'sm',
                      color: '#F97316',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: 'การเปลี่ยนแปลง',
                      weight: 'bold',
                      color: '#374151',
                      size: 'sm',
                      margin: 'sm'
                    }
                  ],
                  paddingBottom: 'sm'
                },
                ...changes.map(change => ({
                  type: 'text',
                  text: `• ${change}`,
                  size: 'xs',
                  color: '#374151',
                  wrap: true,
                  margin: 'xs'
                }))
              ],
              backgroundColor: '#F3F4F6',
              cornerRadius: 'md',
              paddingAll: 'md'
            },
            {
              type: 'box',
              layout: 'vertical',
              margin: 'lg',
              spacing: 'md',
              contents: [
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '📅',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: dateText,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm',
                      weight: 'bold'
                    }
                  ]
                },
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '⏰',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: `${this.formatTime(schedule.startTime)} - ${this.formatTime(schedule.endTime)} น.`,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                },
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '🚙',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: schedule.vehicle?.registrationNumber || 'ไม่ระบุ',
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                },
                schedule.notes ? {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '📝',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: schedule.notes,
                      wrap: true,
                      color: '#6B7280',
                      size: 'sm'
                    }
                  ]
                } : null
              ].filter(Boolean)
            }
          ],
          paddingAll: 'xl'
        },
        footer: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'button',
              action: {
                type: 'uri',
                label: 'ดูรายละเอียดเพิ่มเติม',
                uri: `${this.baseUrl}/schedule-detail?id=${schedule.id}`
              },
              style: 'primary',
              color: '#F97316',
              height: 'sm'
            }
          ],
          paddingAll: 'md'
        }
      }
    }
  }

  /* -------------------- Cancellation -------------------- */

  buildCancellationMessage(schedule: ScheduleEntry, driver: Driver, reason?: string): any {
    const date = new Date(schedule.startDate)
    const vehicleImageUrl = schedule.vehicle?.imageUrl || 'https://via.placeholder.com/800x400/EF4444/FFFFFF?text=No+Image'
    const dateText = this.formatThaiDateWithDay(date)

    return {
      type: 'flex',
      altText: '❌ ยกเลิกงานขับรถ',
      contents: {
        type: 'bubble',
        size: 'mega',
        header: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'box',
              layout: 'horizontal',
              contents: [
                {
                  type: 'text',
                  text: '❌',
                  size: 'xl',
                  flex: 0
                },
                {
                  type: 'text',
                  text: 'ยกเลิกงานขับรถ',
                  weight: 'bold',
                  size: 'lg',
                  color: '#FFFFFF',
                  margin: 'sm'
                }
              ]
            }
          ],
          backgroundColor: '#EF4444',
          paddingAll: 'lg'
        },
        hero: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'image',
              url: vehicleImageUrl,
              size: 'full',
              aspectMode: 'cover',
              aspectRatio: '20:13'
            },
            {
              type: 'box',
              layout: 'vertical',
              contents: [
                {
                  type: 'box',
                  layout: 'vertical',
                  contents: [
                    {
                      type: 'text',
                      text: schedule.vehicle?.registrationNumber || 'ไม่ระบุ',
                      size: 'xs',
                      color: '#ffffff',
                      weight: 'bold'
                    }
                  ],
                  backgroundColor: '#1F2937',
                  cornerRadius: 'md',
                  paddingAll: '8px',
                  paddingStart: '12px',
                  paddingEnd: '12px'
                }
              ],
              position: 'absolute',
              offsetEnd: '18px',
              offsetBottom: '18px'
            }
          ],
          paddingAll: '0px'
        },
        body: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'text',
              text: `${driver.fullName}`,
              weight: 'bold',
              size: 'xl',
              color: '#1F2937'
            },
            {
              type: 'text',
              text: 'งานขับรถของคุณถูกยกเลิก',
              size: 'sm',
              color: '#6B7280',
              margin: 'sm'
            },
            reason ? {
              type: 'box',
              layout: 'vertical',
              margin: 'lg',
              spacing: 'sm',
              contents: [
                {
                  type: 'box',
                  layout: 'horizontal',
                  contents: [
                    {
                      type: 'text',
                      text: '💬',
                      size: 'sm',
                      color: '#EF4444',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: 'เหตุผล',
                      weight: 'bold',
                      color: '#374151',
                      size: 'sm',
                      margin: 'sm'
                    }
                  ],
                  paddingBottom: 'sm'
                },
                {
                  type: 'text',
                  text: reason,
                  size: 'xs',
                  color: '#374151',
                  wrap: true
                }
              ],
              backgroundColor: '#FEE2E2',
              cornerRadius: 'md',
              paddingAll: 'md'
            } : null,
            {
              type: 'box',
              layout: 'vertical',
              margin: 'lg',
              spacing: 'md',
              contents: [
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '📅',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: dateText,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm',
                      weight: 'bold'
                    }
                  ]
                },
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '⏰',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: `${this.formatTime(schedule.startTime)} - ${this.formatTime(schedule.endTime)} น.`,
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                },
                {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '🚙',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: schedule.vehicle?.registrationNumber || 'ไม่ระบุ',
                      wrap: true,
                      color: '#1F2937',
                      size: 'sm'
                    }
                  ]
                },
                schedule.notes ? {
                  type: 'box',
                  layout: 'horizontal',
                  spacing: 'sm',
                  contents: [
                    {
                      type: 'text',
                      text: '📝',
                      size: 'md',
                      flex: 0
                    },
                    {
                      type: 'text',
                      text: schedule.notes,
                      wrap: true,
                      color: '#6B7280',
                      size: 'sm'
                    }
                  ]
                } : null
              ].filter(Boolean)
            }
          ].filter(Boolean),
          paddingAll: 'xl'
        },
        footer: {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'button',
              action: {
                type: 'uri',
                label: 'ดูรายละเอียดเพิ่มเติม',
                uri: `${this.baseUrl}/schedule-detail?id=${schedule.id}`
              },
              style: 'primary',
              color: '#EF4444',
              height: 'sm'
            }
          ],
          paddingAll: 'md'
        }
      }
    }
  }
}

/* -------- Export singleton -------- */
export const flexMessageBuilder = new LineFlexMessageBuilder()
